import { useEffect, useMemo } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useDeliveryDays } from '../../hooks/use-delivery-days.hook';
import { NO_DELIVERY_DAY_MESSAGE } from '../../constants/delivery-schedule.constants';
import { deliveryDayFromOrder } from '../../utils/format-delivery-schedule.util';
import { userAddressToDeliveryAddress } from '../../utils/user-address-to-delivery-address.utils';
import { DeliveryAddress, DeliveryDay, UserAddress } from '../../types/delivery-selection.type';
import { Order } from '../../types/order.type';
import { DeliveryDaySelect } from './DeliveryDaySelect.component';

type Props = {
  addresses: UserAddress[];
  selectedAddress: DeliveryAddress | null;
  selectedDay: DeliveryDay | null;
  onAddressChange: (address: DeliveryAddress | null) => void;
  onBillingAddressChange: (address: DeliveryAddress | null) => void;
  onDayChange: (day: DeliveryDay | null) => void;
  lastOrder?: Order | null;
};

type PostalAddress = Pick<DeliveryAddress, 'address' | 'zipCode' | 'city'>;

const findUserAddress = (addresses: UserAddress[], postalAddress: PostalAddress | undefined): UserAddress | undefined =>
  postalAddress
    ? addresses.find(
        (item) =>
          item.street === postalAddress.address &&
          item.zipCode === postalAddress.zipCode &&
          item.city === postalAddress.city
      )
    : undefined;

export function HomeDeliverySection({
  addresses,
  selectedAddress,
  selectedDay,
  onAddressChange,
  onBillingAddressChange,
  onDayChange,
  lastOrder,
}: Props) {
  const lastOrderInfos = lastOrder?.deliveryInfos;

  const lastOrderAddress = useMemo(
    () => findUserAddress(addresses, lastOrderInfos?.deliveryAddress),
    [addresses, lastOrderInfos]
  );

  const lastOrderBillingAddress = useMemo(
    () => findUserAddress(addresses, lastOrderInfos?.deliveryBillingAddress),
    [addresses, lastOrderInfos]
  );

  const lastOrderDay = useMemo(
    () =>
      lastOrderInfos?.deliveryDate && lastOrderInfos.deliverySchedule ? deliveryDayFromOrder(lastOrderInfos) : null,
    [lastOrderInfos]
  );

  const defaultDeliveryAddress = useMemo(() => {
    if (selectedAddress) {
      const selectedUserAddress =
        addresses.find((item) => item.id === selectedAddress._id) ?? findUserAddress(addresses, selectedAddress);
      if (selectedUserAddress) return selectedUserAddress;
    }
    return lastOrderAddress ?? addresses.find((item) => item.isMain) ?? addresses[0];
  }, [addresses, selectedAddress, lastOrderAddress]);

  const defaultBillingAddress = useMemo(
    () => lastOrderBillingAddress ?? addresses.find((item) => item.isBill) ?? defaultDeliveryAddress,
    [addresses, lastOrderBillingAddress, defaultDeliveryAddress]
  );

  const { isLoading, deliveryCity, days, hasNoDeliverableCity } = useDeliveryDays({
    zipCode: defaultDeliveryAddress?.zipCode ?? '',
  });

  const handleChangeAddress = (addressId: string) => {
    const userAddress = addresses.find((item) => item.id === addressId);
    const deliveryAddress = userAddress ? userAddressToDeliveryAddress(userAddress) : null;
    onAddressChange(deliveryAddress);
    onBillingAddressChange(deliveryAddress);
    onDayChange(null);
  };

  useEffect(() => {
    if (!selectedAddress && defaultDeliveryAddress) {
      onAddressChange(userAddressToDeliveryAddress(defaultDeliveryAddress));
      if (defaultBillingAddress) {
        onBillingAddressChange(userAddressToDeliveryAddress(defaultBillingAddress));
      }
    }
  }, [selectedAddress, defaultDeliveryAddress, defaultBillingAddress, onAddressChange, onBillingAddressChange]);

  useEffect(() => {
    if (!selectedDay && days.length > 0) {
      const lastOrderMatchingDay = lastOrderDay
        ? days.find((day) => day.date.isSame(lastOrderDay.date, 'day'))
        : undefined;
      onDayChange(lastOrderMatchingDay ?? days[0]);
    }
  }, [selectedDay, days, lastOrderDay, onDayChange]);

  if (addresses.length === 0) {
    return (
      <>
        <p>Aucune adresse pour cet utilisateur</p>
        <div />
      </>
    );
  }

  const renderDeliveryDays = () => {
    if (hasNoDeliverableCity) {
      return <FormHelperText error>Aucune ville de livraison active pour ce code postal</FormHelperText>;
    }
    if (isLoading) return <Skeleton />;
    if (days.length > 0 && selectedAddress) {
      return <DeliveryDaySelect days={days} selectedDay={selectedDay} onChange={onDayChange} />;
    }
    if (selectedAddress && deliveryCity !== null) {
      return <FormHelperText error>{NO_DELIVERY_DAY_MESSAGE}</FormHelperText>;
    }
    return <div />;
  };

  return (
    <>
      <FormControl variant="outlined" size="small">
        <InputLabel>Adresse</InputLabel>
        <Select
          value={defaultDeliveryAddress?.id ?? ''}
          label="Adresse"
          onChange={(event) => handleChangeAddress(event.target.value as string)}
        >
          {addresses.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.isMain && 'Principal - '} {item.street} - {item.city} {item.zipCode}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {renderDeliveryDays()}
    </>
  );
}
